import {createSelector} from 'reselect';
import { totalDataPerDay, dataIndexedPerDay, getCombinedData, getCombinedConfiguredItemStats, getValidConfiguredItems } from '../../utils/statsUtils';
import { getDSAPeakBuffer } from '../dsaSelectors';
import { findIndex, sortBy } from 'lodash';

import moment from 'moment';

import {
  getSubAreas,
  getOrderedSubAreas,
  makeGetActiveAreas
} from './areaSelectors';

// Getters for full data sets
const getConfiguredItems = (state) => state.dsaConfig && state.dsaConfig.configuredItems || [];
const getDSAConfiguredItems = (state) => state.dsa && state.dsa.dsaConfiguredItems || [];
const getCorrelations = (state) => state.dsaConfig && state.dsaConfig.correlations || [];
const getDSACorrelations = (state) => state.dsa && state.dsa.dsaCorrelations || [];
const getCorrelationCategories = (state) => state.dsaConfig && state.dsaConfig.correlationCategories || [];

const getDSAConfiguredItemsWithTotalDataPerDay = createSelector(
  [getDSAConfiguredItems],
  (dsaConfiguredItems) => (
    dsaConfiguredItems.map(dsaConfiguredItem => (
      Object.assign({}, dsaConfiguredItem, { totalDataPerDay: totalDataPerDay(dsaConfiguredItem), totalIndexed: dataIndexedPerDay(dsaConfiguredItem)})
    ))
  )
);

// Getters for area data sets
// const getArea = (state, props) => props && props.area;
const getSubAreaIds = (state, props) => {
  return state.dsaConfig && props.area && state.dsaConfig.subAreas && state.dsaConfig.subAreas.filter(subarea => subarea.area === props.area.id).map((s) => s.id) || [];
};
// Getters for subarea data sets
const getSubArea = (state, props) => props && props.subarea;
const getConfiguredItem = (state, props) => props && props.configuredItem;


const getConfiguredItemCorrelations = (state, props) => {
  let configuredItem = getConfiguredItem(state, props);
  let correlations = getCorrelations(state);
  return correlations.filter(uc => configuredItem.correlationIds.indexOf(uc.id) >= 0);
};

export const makeGetAreaSourcesCompletionProgress = () => {
  return createSelector(
    [getSubAreaIds, getConfiguredItems, getDSAConfiguredItems],
    (subAreaIds, questions, answers) => {
      let progress = {
        current: 0,
        total: subAreaIds.length
      };

      let combinedConfiguredItems = getCombinedData(questions, answers, 'configureditem');
      let enabledConfiguredItems = combinedConfiguredItems.filter(item => !item.excluded);

      subAreaIds.forEach(subAreaId => {
        let subAreaConfiguredItems = enabledConfiguredItems.filter(q => q.subarea === subAreaId);
        let answeredSubAreaConfiguredItems = subAreaConfiguredItems.filter(item => item.completed);

        if (answeredSubAreaConfiguredItems.length === subAreaConfiguredItems.length) progress.current += 1;
      });

      return progress;
    }
  );
};

export const makeGetAreaSourcesProgress = () => {
  return createSelector(
    [getSubAreaIds, getConfiguredItems, getDSAConfiguredItems],
    (subAreaIds, questions, answers) => {
      let progress = {
        current: 0,
        total: 0
      };

      let combinedConfiguredItems = getCombinedData(questions, answers, 'configureditem');
      let enabledConfiguredItems = combinedConfiguredItems.filter(item => !item.excluded);

      subAreaIds.forEach(subAreaId => {
        let subAreaConfiguredItems = enabledConfiguredItems.filter(q => q.subarea === subAreaId);
        let answeredSubAreaConfiguredItems = subAreaConfiguredItems.filter(item => item.completed);

        progress.total += subAreaConfiguredItems.length;
        progress.current += answeredSubAreaConfiguredItems.length;
      });

      return progress;
    }
  );
};

export const makeGetSubAreaSourcesProgress = () => {
  return createSelector(
    [getSubArea, getConfiguredItems, getDSAConfiguredItems],
    (subArea, questions, answers) => {
      let progress = {
        current: 0,
        total: 0
      };

      let combinedConfiguredItems = getCombinedData(questions, answers, 'configureditem');

      let enabledConfiguredItems = combinedConfiguredItems.filter(item => !item.excluded);
      let subAreaConfiguredItems = enabledConfiguredItems.filter(q => q.subarea === subArea.id);
      let answeredSubAreaConfiguredItems = subAreaConfiguredItems.filter(item => item.completed);

      progress.total += subAreaConfiguredItems.length;
      progress.current += answeredSubAreaConfiguredItems.length;

      return progress;
    }
  );
};

export const makeGetAreaSourcesStats = () => {
  return createSelector(
    [getSubAreaIds, getConfiguredItems, getDSAConfiguredItems, getDSAPeakBuffer],
    (subAreaIds, questions, answers, peakBuffer) => {

      let combinedConfiguredItems = getCombinedData(questions, answers, 'configureditem');
      let validConfiguredItems = getValidConfiguredItems(combinedConfiguredItems);

      let areaConfiguredItems = validConfiguredItems.filter(item => subAreaIds.indexOf(item.subarea) >= 0);

      return getCombinedConfiguredItemStats(areaConfiguredItems, peakBuffer);
    }
  );
};

export const makeGetSubAreaSourcesStats = () => {
  return createSelector(
    [getSubArea, getConfiguredItems, getDSAConfiguredItems, getDSAPeakBuffer],
    (subArea, questions, answers, peakBuffer) => {

      let combinedConfiguredItems = getCombinedData(questions, answers, 'configureditem');
      let validConfiguredItems = getValidConfiguredItems(combinedConfiguredItems);

      let subAreaConfiguredItems = validConfiguredItems.filter(q => q.subarea === subArea.id);

      return getCombinedConfiguredItemStats(subAreaConfiguredItems, peakBuffer);
    }
  );
};


export const makeGetConfiguredItemCorrelations = () => {
  return createSelector(
    [getConfiguredItemCorrelations, getCorrelations, getCorrelationCategories, getDSACorrelations],
    (configuredItemCorrelations, correlations, correlationCategories, dsaCorrelations) => {
      return correlationCategories.map(ucc => {
        let categoryCorrelations = configuredItemCorrelations.filter(uc => uc.category === ucc.id);
        return {
          title: ucc.title,
          correlations: categoryCorrelations.map(uc => {
            let dsaCorrelation = dsaCorrelations.find(duc => duc.correlation === uc.id);
            return {
              title: uc.title,
              required: !!dsaCorrelation.answer
            };
          })
        };
      }).filter(ucc => ucc.correlations.length > 0);
    }
  );
};

const getJoinedDSAConfiguredItems = createSelector(
  [getConfiguredItems, getDSAConfiguredItemsWithTotalDataPerDay],
  (configuredItems, dsaConfiguredItems) => {
    return dsaConfiguredItems.map(dsaConfiguredItem => {
      let configuredItem = configuredItems.find(i => i.id === dsaConfiguredItem.configureditem);
      let newobject = {};
        if (dsaConfiguredItem.description !== '' && dsaConfiguredItem.description) {
            newobject.description = dsaConfiguredItem.description;
        }
      return Object.assign({}, dsaConfiguredItem, configuredItem, newobject);
    });
  }
);

const getSortedJoinedDSAConfiguredItems = createSelector(
  [getOrderedSubAreas, getJoinedDSAConfiguredItems],
  (subAreas, configuredItems) => {
    return [...configuredItems].sort((a, b) => {
      const subAreaIndexA = findIndex(subAreas, subArea => subArea.id === a.subarea);
      const subAreaIndexB = findIndex(subAreas, subArea => subArea.id === b.subarea);

      return subAreaIndexA - subAreaIndexB || a.order - b.order;
    });
  }
);

export const getCompletedActiveConfiguredItems = (state) => {
  const subAreas = getSubAreas(state);
  const configuredItems = getSortedJoinedDSAConfiguredItems(state);
  const getActiveAreas = makeGetActiveAreas();
  const activeAreas = getActiveAreas(state);
  const activeAreaIds = activeAreas.map(a => a.id);
  const activeSubAreas = subAreas.filter(subArea => activeAreaIds.includes(subArea.area));
  const activeSubAreaIds = activeSubAreas.map(a => a.id);

  return configuredItems.filter(i => (
    !i.excluded &&
    i.completed &&
    activeSubAreaIds.includes(i.subarea)
  ));
};

export const getCompletedActiveConfiguredItemsCount = createSelector(
  [getCompletedActiveConfiguredItems],
  configuredItems => configuredItems.reduce((acc, item) => acc + item.totalItems, 0)
);


export const getTotalDataPerDayForCompletedActiveConfiguredItems = createSelector(
  [getCompletedActiveConfiguredItems],
  (completedActiveConfiguredItems) => {
    return completedActiveConfiguredItems.reduce((acc, item) => acc + totalDataPerDay(item), 0);
  }
);

export const makeGetDSAConfiguredItemsLastModifiedAt = () => (
  createSelector(
    [getDSAConfiguredItems],
    (dsaConfiguredItems) => {
      const mostRecentlyModifiedItem = sortBy(dsaConfiguredItems, item => -1 * moment(item.updatedAt))[0];
      return mostRecentlyModifiedItem.updatedAt;
    }
  )
);



// WEBPACK FOOTER //
// ./src/selectors/dsa/sourcesSelectors.js